import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronLeft, ChevronRight, LayoutGrid, Layers, PhoneMissed, Plus, TriangleAlert } from 'lucide-react';
import { getFeed, patchTask, type CalendarFeed, type FeedTask } from '@/api/calendar';
import { Header } from '@/components/Header';
import { EmptyState } from '@/components/EmptyState';
import { BlockSheet, ChooseSheet, MeetingSheet, TaskSheet } from '@/components/calendar/NewItemSheets';
import { useProject } from '@/contexts/ProjectProvider';
import { calKeys, useFeed, useHosts, useMyOpenTasks } from '@/hooks/useCalendarData';
import { useOpenDeal } from '@/hooks/useOpenDeal';
import {
  agendaFor, dotsFor, moveToTodayPatch, overdueFromFeed, overdueFromOpen, withTaskDone, type AgendaItem, type OverdueTask,
} from '@/lib/agenda';
import {
  addDays, dayKey, dayTitle, dayWord, feedRange, hhmm, monthGrid, monthTitle, noonOf, visibleDays, WD, weekDays,
} from '@/lib/calendarDates';
import { useWorkUi } from '@/store/workUi';
import { useToast } from '@/store/toast';
import { ApiError } from '@/lib/http';

const plural = (n: number, a: string, b: string, c: string) => {
  const m = n % 10, h = n % 100;
  return m === 1 && h !== 11 ? a : m >= 2 && m <= 4 && (h < 12 || h > 14) ? b : c;
};

/**
 * «Календарь» — как календарь CRM (CrmCalendar.tsx), но с неделей наверху и месяцем по кнопке.
 * День: встречи (занимают время) и быстрые задачи (галочка «сделано»). На «Сегодня» сверху — «Просрочено»
 * (мои несделанные задачи прошлых дней, пока не сделаны или не перенесены кнопкой «На сегодня»).
 */
export default function CalendarPage() {
  const { status, projectId } = useProject();
  const qc = useQueryClient();
  const toast = useToast((t) => t.show);
  const openDeal = useOpenDeal();
  const { calDay, calMonth, calWho, setCal } = useWorkUi();
  const today = dayKey(new Date());
  const [menu, setMenu] = useState(false);
  const [sheet, setSheet] = useState<null | 'choose' | 'task' | 'meeting' | 'block'>(null);
  const [hidden, setHidden] = useState<string[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);

  const days = useMemo(() => visibleDays(calDay, calMonth), [calDay, calMonth]);
  const feed = useFeed(days, calWho);
  const canSeeOthers = !!feed.data?.canSeeOthers;
  const hosts = useHosts(canSeeOthers);
  const names = useMemo(() => new Map((hosts.data ?? []).map((h) => [h.id, h.name])), [hosts.data]);
  const mine = useMyOpenTasks();

  // Чужой календарь недоступен (403) — возвращаемся к своему, а не показываем пустоту «как у коллеги».
  const forbidden = feed.error instanceof ApiError && feed.error.status === 403;
  const forbiddenText = forbidden ? feed.error?.message : null;
  useEffect(() => {
    if (forbiddenText && calWho !== 'me') { setCal({ calWho: 'me' }); toast(forbiddenText || 'Чужой календарь доступен руководителю'); }
  }, [forbiddenText, calWho, setCal, toast]);

  // Запасной путь «Просрочено», если открытые задачи не прочитались вовсе: выдача за прошлые 30 дней.
  const showOverdue = calDay === today && calWho === 'me';
  const past = useQuery({
    queryKey: calKeys.past(projectId, today),
    enabled: showOverdue && mine.isError,
    queryFn: () => { const r = feedRange([addDays(today, -30), addDays(today, -1)]); return getFeed(r.from, r.to, 'me'); },
    staleTime: 60_000,
  });
  const overdue: OverdueTask[] = !showOverdue ? []
    : mine.data ? overdueFromOpen(mine.data, today, hidden)
      : overdueFromFeed(past.data?.tasks, today, hidden);

  const agenda = agendaFor(feed.data, calDay);
  const allDay = agenda.filter((i) => !i.time);
  const timed = agenda.filter((i) => i.time);

  const refreshAll = () => void qc.invalidateQueries({ queryKey: calKeys.all(projectId) });

  /** Галочка в повестке: сразу в кэше, при отказе — назад со словами сервера. */
  const toggle = async (t: FeedTask) => {
    const done = !t.done;
    const key = ['crm', 'cal', projectId, 'feed'];
    const before = qc.getQueriesData<CalendarFeed>({ queryKey: key });
    qc.setQueriesData<CalendarFeed>({ queryKey: key }, (f) => withTaskDone(f, t.id, done));
    setBusyId(t.id);
    try {
      await patchTask(t.id, { done });
      if (done) toast(`Сделано: ${t.title}`);
      refreshAll();
    } catch (e) {
      for (const [k, v] of before) qc.setQueryData(k, v);
      toast(e instanceof Error ? e.message : 'Отметка не сохранилась');
    } finally {
      setBusyId(null);
    }
  };

  /** Просроченная: «сделано» или «На сегодня». Строка уходит сразу, при отказе возвращается. */
  const overdueAct = async (t: OverdueTask, act: 'done' | 'today') => {
    setHidden((h) => [...h, t.id]);
    setBusyId(t.id);
    try {
      await patchTask(t.id, act === 'done' ? { done: true } : moveToTodayPatch(t, today));
      toast(act === 'done' ? `Сделано: ${t.title}` : 'Перенесено на сегодня');
      refreshAll();
    } catch (e) {
      setHidden((h) => h.filter((x) => x !== t.id));
      toast(e instanceof Error ? e.message : 'Не удалось изменить задачу');
    } finally {
      setBusyId(null);
    }
  };

  if (status !== 'ready') return <><Header title="Календарь" subtitle="Загружаем проект…" /><div className="scr" /></>;
  if (!projectId) {
    return <><Header title="Календарь" /><div className="scr"><EmptyState icon={Layers} title="Проект не выбран" text="Выберите проект в разделе «Ещё»." /></div></>;
  }

  const whoName = calWho === 'me' ? 'Мой календарь' : calWho === 'all' ? 'Вся команда' : names.get(calWho) ?? 'Коллега';
  const showWho = calWho !== 'me';
  const canTick = (t: FeedTask) => t.own || canSeeOthers;

  const cell = (key: string, opts: { inMonth?: boolean; wd?: string }) => {
    const dots = dotsFor(feed.data, key, today);
    const d = noonOf(key);
    return (
      <button key={key} type="button" className={`dcell${key === today ? ' today' : ''}${opts.inMonth === false ? ' off' : ''}`}
        aria-pressed={key === calDay} aria-label={`${dayTitle(key, today)}${dots.length ? `, дел: ${dots.length}` : ''}`}
        onClick={() => setCal({ calDay: key })}>
        {opts.wd && <small>{opts.wd}</small>}
        <b className="tnum">{d.getDate()}</b>
        <span className="dots" aria-hidden>{dots.map((c, i) => <i key={i} className={c} />)}</span>
      </button>
    );
  };

  const shiftMonth = (n: number) => {
    const d = noonOf(calDay);
    setCal({ calDay: dayKey(new Date(d.getFullYear(), d.getMonth() + n, 1, 12)) });
  };

  const menuEl = menu && (
    <>
      <div className="scrim" onClick={() => setMenu(false)} aria-hidden />
      <div className="menu" role="menu">
        <button type="button" role="menuitemradio" aria-checked={calWho === 'me'} onClick={() => { setCal({ calWho: 'me' }); setMenu(false); }}>
          <Check aria-hidden />Мой календарь
        </button>
        {canSeeOthers ? (
          <>
            <button type="button" role="menuitemradio" aria-checked={calWho === 'all'} onClick={() => { setCal({ calWho: 'all' }); setMenu(false); }}>
              <Check aria-hidden />Вся команда
            </button>
            {(hosts.data ?? []).filter((h) => !h.me).length > 0 && <hr />}
            {(hosts.data ?? []).filter((h) => !h.me).map((h) => (
              <button key={h.id} type="button" role="menuitemradio" aria-checked={calWho === h.id} onClick={() => { setCal({ calWho: h.id }); setMenu(false); }}>
                <Check aria-hidden />{h.name}
              </button>
            ))}
          </>
        ) : (
          <div className="lock">Календари коллег видит руководитель</div>
        )}
      </div>
    </>
  );

  const taskRow = (t: FeedTask) => {
    const sub = [t.entityName, showWho && t.assigneeUserId ? names.get(t.assigneeUserId) : null].filter(Boolean).join(', ');
    return (
      <div key={t.id} className={`ev${t.done ? ' done' : ''}`}>
        <span className="tcol tnum">{t.startsAt ? hhmm(t.startsAt) : ''}</span>
        <button type="button" className="cbw" disabled={!canTick(t) || busyId === t.id} onClick={() => void toggle(t)}
          aria-label={t.done ? `Вернуть в работу: ${t.title}` : `Отметить сделанной: ${t.title}`} aria-pressed={t.done}>
          <span className="cb"><Check aria-hidden /></span>
        </button>
        <button type="button" className="body" onClick={() => t.entityType === 'deal' && void openDeal(t.entityId)}
          disabled={t.entityType !== 'deal'} style={{ color: 'inherit' }}>
          <span>{t.title}</span>
          {t.templateKey === 'call_back' && <span className="auto"><PhoneMissed size={11} aria-hidden />после пропущенного</span>}
          {(sub || (t.done && t.doneReason)) && <small>{t.done && t.doneReason ? `Закрыта сама: ${t.doneReason}` : sub}</small>}
        </button>
      </div>
    );
  };

  const itemRow = (i: AgendaItem) => {
    if (i.kind === 'task') return taskRow(i.task);
    if (i.kind === 'meeting') {
      const m = i.meeting;
      const sub = [m.deal?.title ?? m.guestName, showWho && m.staffUserId ? names.get(m.staffUserId) : null].filter(Boolean).join(', ');
      return (
        <button key={m.id} type="button" className="ev meet" onClick={() => m.dealId && void openDeal(m.dealId)}
          aria-label={`Встреча ${i.time}, ${m.durationMin} мин: ${m.title ?? m.guestName}`}>
          <span className="tcol tnum">{i.time}<small>{m.durationMin} мин</small></span>
          <span className="body"><b>{m.title ?? m.guestName}</b>{sub && <small>{sub}</small>}</span>
        </button>
      );
    }
    const b = i.block;
    const label = b.titleHidden || !b.title ? 'Занято' : b.title;
    return (
      <div key={b.id} className="ev busy">
        <span className="tcol tnum">{i.time ?? ''}{!b.allDay && <small>до {hhmm(b.endsAt)}</small>}</span>
        <span className="body"><span>{label}</span>{showWho && b.staffUserId && <small>{names.get(b.staffUserId)}</small>}</span>
      </div>
    );
  };

  return (
    <>
      <Header
        title={whoName}
        subtitle={monthTitle(calDay)}
        onTitleClick={() => setMenu((m) => !m)}
        titleExpanded={menu}
        menu={menuEl}
        actions={
          <>
            {calDay !== today && <button type="button" className="mini" style={{ marginTop: 4 }} onClick={() => setCal({ calDay: today })}>Сегодня</button>}
            <button type="button" className="ibtn" aria-pressed={calMonth} aria-label={calMonth ? 'Показать неделю' : 'Показать месяц'} onClick={() => setCal({ calMonth: !calMonth })}>
              <LayoutGrid aria-hidden />
            </button>
          </>
        }
      />
      {calMonth ? (
        <div className="month">
          <div className="week" style={{ padding: '0 0 4px' }}>
            <button type="button" className="ibtn" aria-label="Прошлый месяц" onClick={() => shiftMonth(-1)}><ChevronLeft aria-hidden /></button>
            <span style={{ flex: 1, textAlign: 'center', fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>{monthTitle(calDay)}</span>
            <button type="button" className="ibtn" aria-label="Следующий месяц" onClick={() => shiftMonth(1)}><ChevronRight aria-hidden /></button>
          </div>
          <div className="mgrid">
            {WD.map((w) => <span key={w} className="wd">{w}</span>)}
            {monthGrid(calDay).map((c) => cell(c.key, { inMonth: c.inMonth }))}
          </div>
        </div>
      ) : (
        <div className="week">
          <button type="button" className="ibtn" aria-label="Прошлая неделя" onClick={() => setCal({ calDay: addDays(calDay, -7) })}><ChevronLeft aria-hidden /></button>
          <div className="days">{weekDays(calDay).map((k, i) => cell(k, { wd: WD[i] }))}</div>
          <button type="button" className="ibtn" aria-label="Следующая неделя" onClick={() => setCal({ calDay: addDays(calDay, 7) })}><ChevronRight aria-hidden /></button>
        </div>
      )}

      <div className="scr" style={{ borderTop: '1px solid var(--line)', position: 'relative' }}>
        <div className="agenda">
          {calWho === 'all' && <p className="calnote">Показана вся команда. Менять можно своё; руководитель — и задачи своей ветки.</p>}

          {overdue.length > 0 && (
            <section aria-label="Просрочено">
              <div className="ah red">Просрочено<span className="n tnum">{mine.data ? Math.max(overdue.length, mine.data.overdue - hidden.length) : overdue.length}</span></div>
              {overdue.map((t) => (
                <div key={t.id} className="ev late">
                  <span className="tcol" />
                  <button type="button" className="cbw" disabled={busyId === t.id} onClick={() => void overdueAct(t, 'done')} aria-label={`Отметить сделанной: ${t.title}`}>
                    <span className="cb"><Check aria-hidden /></span>
                  </button>
                  <button type="button" className="body" disabled={t.entityType !== 'deal'} onClick={() => t.entityType === 'deal' && void openDeal(t.entityId)} style={{ color: 'inherit' }}>
                    <span>{t.title}</span>
                    <small className="late">{[t.entityName, `срок был ${dayWord(t.dueDate, today)}${t.startsAt ? ` в ${hhmm(t.startsAt)}` : ''}`].filter(Boolean).join(', ')}</small>
                  </button>
                  <button type="button" className="mini" disabled={busyId === t.id} onClick={() => void overdueAct(t, 'today')}>На сегодня</button>
                </div>
              ))}
            </section>
          )}

          <div className="ah">{dayTitle(calDay, today)}<span className="n tnum">{agenda.length ? `${agenda.length} ${plural(agenda.length, 'дело', 'дела', 'дел')}` : ''}</span></div>
          {feed.isPending && <div style={{ padding: '8px 18px' }}><div className="sk" style={{ height: 44, marginBottom: 8 }} /><div className="sk" style={{ height: 44 }} /></div>}
          {feed.isError && !forbidden && (
            <EmptyState icon={TriangleAlert} title="Не удалось прочитать календарь" text={feed.error.message} action={{ label: 'Повторить', onClick: () => void feed.refetch() }} />
          )}
          {feed.isSuccess && agenda.length === 0 && <div className="empty" style={{ padding: 24 }}>На этот день ничего нет.</div>}
          {allDay.map(itemRow)}
          {timed.map(itemRow)}
        </div>
        <div style={{ height: 90 }} />
      </div>
      <button type="button" className="fab" aria-label="Добавить в календарь" onClick={() => setSheet('choose')}>
        <Plus aria-hidden />
      </button>

      {sheet === 'choose' && <ChooseSheet onClose={() => setSheet(null)} onPick={(k) => setSheet(k)} />}
      {sheet === 'task' && <TaskSheet day={calDay < today ? today : calDay} hosts={hosts.data ?? []} canAssign={canSeeOthers} onClose={() => setSheet(null)} />}
      {sheet === 'meeting' && <MeetingSheet day={calDay < today ? today : calDay} defaultMin={feed.data?.settings.defaultMeetingMin} onClose={() => setSheet(null)} />}
      {sheet === 'block' && <BlockSheet day={calDay < today ? today : calDay} onClose={() => setSheet(null)} />}
    </>
  );
}
